import { useRef, useState, useMemo } from 'react';
import { Link, useParams } from 'react-router-dom';
import {
  Store, Loader2, Sparkles, Clock, FileText,
  ImagePlus, Package, Plus, ArrowRight, Eye, EyeOff,
  ShoppingBag, BarChart3,
} from 'lucide-react';
import { Textarea } from '@/components/ui/textarea';
import { FarmlyRichTextField } from '@/components/content/FarmlyRichTextField';
import { Label as FormLabel } from '@/components/ui/label';
import { Button } from '@/components/ui/button';
import { Switch } from '@/components/ui/switch';
import { Label } from '@/components/ui/label';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { ProducerLayout } from '@/components/producer';
import { FeatureBlockedBanner } from '@/components/producer/FeatureBlockedBanner';
import type { ShopBlockReason } from '@/components/producer/FeatureBlockedBanner';
import { useProducerById, useUpdateProducer } from '@/hooks/useMyProducers';
import { useProducerProducts } from '@/hooks/useProducerProducts';
import { useProductCoverImages } from '@/hooks/useProductCoverImages';
import { useProducerUsage } from '@/hooks/useProducerUsage';
import { UsageBadge } from '@/components/producer/UsageBadge';
import { SoftLimitMessage } from '@/components/producer/SoftLimitMessage';
import { useProducerEffectiveFeatures } from '@/hooks/useProducerEffectiveFeatures';
import { evaluateShopGate, type ShopGateReasonKey } from '@/lib/shop/shopGate';
import { useToast } from '@/hooks/use-toast';
import { useShopKpis, KPI_PERIOD_OPTIONS, type KpiPeriod } from '@/hooks/useShopKpis';
import { formatMoney } from '@/lib/formatMoney';
import { ShareStudioSheet } from '@/features/share/ShareStudioSheet';
import { EntityShareAction } from '@/features/share/EntityShareAction';
import { mapFarmshopToShareTarget } from '@/features/share/mappers/mapFarmshopToShareTarget';
import { MediaImageManager } from '@/components/media/MediaImageManager';
import { useMediaUpload } from '@/hooks/useMediaUpload';
import { useShopCoverUrl } from '@/hooks/useProducerShopImages';
import { useCoverImageUrl } from '@/hooks/useMediaAssets';
import { EditorLayout } from '@/components/editor/EditorLayout';
import { EditorActionCard } from '@/components/editor/EditorActionCard';
import { EditorSidebarSection } from '@/components/editor/EditorSidebar';
import { EditorSectionCard, EditorSectionItem } from '@/components/editor/EditorSection';
import { FarmShopPagePreview } from '@/components/farmshop/FarmShopPagePreview';
import { producerShopPath } from '@/lib/paths';
import { features } from '@/config/features';

const SHOP_STATUS_OPTIONS = [
  { value: 'open', label: 'Öppet' },
  { value: 'closed', label: 'Stängt' },
  { value: 'seasonal', label: 'Säsongsöppet' },
] as const;

export default function ProducerShopOverview() {
  const { farmId } = useParams<{ farmId: string }>();
  const { producer, isLoading: producerLoading } = useProducerById(farmId);
  const { products, loading: productsLoading } = useProducerProducts(farmId);
  const { data: usage } = useProducerUsage(farmId);
  const [kpiDays, setKpiDays] = useState<KpiPeriod>(30);
  const { data: kpis, isLoading: kpisLoading } = useShopKpis(farmId, kpiDays);
  const { isFeatureEnabled } = useProducerEffectiveFeatures(farmId);
  const updateProducer = useUpdateProducer();
  const { toast } = useToast();
  const toggleRef = useRef<HTMLDivElement>(null);
  const [activating, setActivating] = useState(false);
  const [showShareStudio, setShowShareStudio] = useState(false);

  // Product cover images for preview
  const productIds = useMemo(() => products.map(p => p.id), [products]);
  const { data: coverImages = {} } = useProductCoverImages(productIds, 320);

  // Shop media uploads
  const shopCoverUpload = useMediaUpload({
    ownerType: 'producer_shop',
    ownerId: farmId ?? null,
    kind: 'cover',
    maxFiles: 1,
  });
  const shopGalleryUpload = useMediaUpload({
    ownerType: 'producer_shop',
    ownerId: farmId ?? null,
    kind: 'gallery',
    maxFiles: 8,
  });

  // Resolve shop hero image with fallback chain: shop cover → producer cover
  const shopCoverUrl = useShopCoverUrl(farmId, 1280);
  const producerCoverUrl = useCoverImageUrl('producer', farmId, 1280);
  const resolvedHeroUrl = shopCoverUrl ?? producerCoverUrl;

  // Farm shop info fields
  const [shopIntro, setShopIntro] = useState('');
  const [shopOpeningHours, setShopOpeningHours] = useState('');
  const [shopStatus, setShopStatus] = useState<string>('closed');
  const [shopInfoDirty, setShopInfoDirty] = useState(false);
  const [shopInfoSaving, setShopInfoSaving] = useState(false);
  const [shopInfoInitialized, setShopInfoInitialized] = useState(false);

  // Sync from producer data
  if (producer && !shopInfoInitialized) {
    setShopIntro(producer.farm_shop_intro || '');
    setShopOpeningHours(producer.farm_shop_opening_hours_text || '');
    setShopStatus((producer as any).farm_shop_status || 'closed');
    setShopInfoInitialized(true);
  }

  const handleSaveShopInfo = async () => {
    if (!farmId) return;
    setShopInfoSaving(true);
    try {
      await updateProducer.mutateAsync({
        id: farmId,
        updates: {
          farm_shop_intro: shopIntro.trim(),
          farm_shop_opening_hours_text: shopOpeningHours.trim(),
          farm_shop_status: shopStatus,
        },
      });
      setShopInfoDirty(false);
      toast({ title: 'Sparat', description: 'Butiksinformation har sparats.' });
    } catch {
      toast({ title: 'Kunde inte spara', variant: 'destructive' });
    } finally {
      setShopInfoSaving(false);
    }
  };

  const hasFarmShopEntitlement = isFeatureEnabled('farm_shop');

  const gate = producer
    ? evaluateShopGate({
        status: producer.status,
        producer_status: producer.producerStatus,
        verified: producer.verified,
        shop_enabled: producer.shop_enabled,
        hasFarmShopEntitlement,
      })
    : null;

  const bannerReasonMap: Partial<Record<ShopGateReasonKey, ShopBlockReason>> = {
    needs_verification: 'verification',
    needs_entitlement: 'entitlement',
    needs_enable: 'disabled',
    needs_public: 'entitlement',
    paused: 'entitlement',
    banned: 'entitlement',
  };

  const shopBlockReason = gate && !gate.allowed ? bannerReasonMap[gate.reason_key] ?? 'entitlement' : null;
  const shopIsHidden = gate?.reason_key === 'needs_enable';
  const showToggle = gate && gate.reason_key !== 'needs_entitlement' && gate.reason_key !== 'needs_public' && gate.reason_key !== 'paused' && gate.reason_key !== 'banned';

  const handleShopEnabledToggle = async (checked: boolean) => {
    if (!farmId) return;
    try {
      await updateProducer.mutateAsync({ id: farmId, updates: { shop_enabled: checked } });
      toast({
        title: checked ? 'Butiken visas nu publikt' : 'Gårdsbutiken är nu dold',
        description: checked
          ? 'Besökare kan se din butik på din gårdssida.'
          : 'Butiken visas inte längre för besökare.',
      });
    } catch {
      toast({ title: 'Kunde inte uppdatera', variant: 'destructive' });
    }
  };

  const handleActivateShop = async () => {
    if (!farmId) return;
    setActivating(true);
    try {
      await updateProducer.mutateAsync({ id: farmId, updates: { shop_enabled: true } });
      toast({
        title: 'Butiken visas nu publikt',
        description: 'Besökare kan se din butik på din gårdssida.',
      });
      setTimeout(() => {
        toggleRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }, 300);
    } catch {
      toast({ title: 'Kunde inte aktivera gårdsbutiken', variant: 'destructive' });
    } finally {
      setActivating(false);
    }
  };

  if (producerLoading) {
    return (
      <ProducerLayout farmId={farmId || ''} producerName="Laddar..." title="Gårdsbutik">
        <div className="flex items-center justify-center py-12">
          <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
        </div>
      </ProducerLayout>
    );
  }

  const activeProducts = products.filter((p) => p.status === 'public');
  const draftProducts = products.filter((p) => p.status === 'draft');

  // Build preview products with images
  const previewProducts = products.map(p => ({
    id: p.id,
    title: p.title,
    price_ore: p.price_ore,
    currency: p.currency,
    status: p.status,
    imageUrl: coverImages[p.id] || null,
  }));

  const shopUrl = producer?.slug ? `/producer/${producer.slug}/shop` : undefined;
  const shopPublicUrl = producer?.slug ? `${window.location.origin}${producerShopPath(producer.slug)}` : null;

  return (
    <ProducerLayout farmId={farmId || ''} producerName={producer?.name} title="Gårdsbutik">
      <div className="space-y-6">
        {/* Gate banner */}
        {shopBlockReason && gate && (
          <FeatureBlockedBanner
            featureName="Gårdsbutik"
            allowedAction="skapa produkter som utkast"
            reason={shopBlockReason}
            customTitle={gate.title_sv}
            customBadge={gate.badge_sv}
            customDescription={gate.message_sv}
            ctaLabel={shopIsHidden ? 'Aktivera gårdsbutik' : undefined}
            onCtaClick={shopIsHidden ? handleActivateShop : undefined}
            ctaLoading={activating}
          />
        )}

        {/* Soft limit message */}
        {usage && (
          <SoftLimitMessage
            used={usage.products_used}
            limit={usage.products_limit}
            resourceName="produkter"
          />
        )}

        {/* Header with usage badge */}
        <div className="flex items-center gap-3">
          <h1 className="text-2xl font-semibold text-foreground">Gårdsbutik</h1>
          {usage && (
            <UsageBadge
              used={usage.products_used}
              limit={usage.products_limit}
              label="Produkter"
            />
          )}
        </div>

        {/* Preview-first layout */}
        <EditorLayout
          main={
            <FarmShopPagePreview
              producerName={producer?.name || ''}
              coverImageUrl={resolvedHeroUrl}
              shopIntro={shopIntro}
              shopStatus={shopStatus}
              openingHours={shopOpeningHours || undefined}
              products={previewProducts}
            />
          }
          sidebar={
            <div className="space-y-5">
              {/* Action card – publish/save/view */}
              <EditorActionCard
                isPublished={producer?.shop_enabled ?? false}
                publishedLabel="Synlig"
                draftLabel="Dold"
                onPublish={() => handleShopEnabledToggle(true)}
                onUnpublish={() => handleShopEnabledToggle(false)}
                publishDisabled={!producer?.verified || !hasFarmShopEntitlement}
                onSave={handleSaveShopInfo}
                isSaving={shopInfoSaving}
                saveDisabled={!shopInfoDirty}
                viewHref={shopUrl}
                secondaryAction={
                  producer?.slug && producer?.shop_enabled ? (
                    <EntityShareAction
                      entityType="product"
                      publicUrl={shopPublicUrl}
                      buttonClassName="w-full gap-1.5 text-xs"
                      socialAction={features.enableShareStudio && producer ? (
                        <Button
                          variant="outline"
                          size="sm"
                          className="gap-2"
                          onClick={() => setShowShareStudio(true)}
                        >
                          <Sparkles className="h-4 w-4" />
                          Share Studio
                        </Button>
                      ) : undefined}
                    />
                  ) : undefined
                }
              />

              {/* Settings sections */}
              <EditorSidebarSection heading="Innehåll">
                <EditorSectionCard>
                  {/* Intro text */}
                  <EditorSectionItem
                    title="Beskrivning"
                    icon={FileText}
                    defaultOpen
                    hint={shopIntro ? 'Redigera' : 'Lägg till'}
                  >
                    <FarmlyRichTextField
                      id="farm-shop-intro"
                      label=""
                      value={shopIntro}
                      onChange={(md) => { setShopIntro(md); setShopInfoDirty(true); }}
                      placeholder="Berätta vad besökare hittar här och hur det känns i gårdsbutiken."
                      maxLength={2000}
                      minHeightPx={100}
                    />
                  </EditorSectionItem>

                  {/* Opening hours */}
                  <EditorSectionItem
                    title="Öppettider"
                    icon={Clock}
                    hint={shopOpeningHours || 'Valfritt'}
                  >
                    <Textarea
                      id="farm-shop-hours"
                      value={shopOpeningHours}
                      onChange={(e) => { setShopOpeningHours(e.target.value); setShopInfoDirty(true); }}
                      placeholder="T.ex. 'Fre–Sön 10–16'"
                      rows={2}
                    />
                    <p className="text-xs text-muted-foreground mt-1.5">
                      Om butikens tider skiljer sig från Besöksöppet.
                    </p>
                  </EditorSectionItem>

                  {/* Status */}
                  <EditorSectionItem
                    title="Status just nu"
                    icon={Store}
                    hint={SHOP_STATUS_OPTIONS.find(o => o.value === shopStatus)?.label}
                  >
                    <RadioGroup
                      value={shopStatus}
                      onValueChange={(v) => { setShopStatus(v); setShopInfoDirty(true); }}
                      className="space-y-2"
                    >
                      {SHOP_STATUS_OPTIONS.map((opt) => (
                        <div key={opt.value} className="flex items-center gap-2">
                          <RadioGroupItem value={opt.value} id={`shop-status-${opt.value}`} />
                          <Label htmlFor={`shop-status-${opt.value}`} className="text-sm font-normal cursor-pointer">
                            {opt.label}
                          </Label>
                        </div>
                      ))}
                    </RadioGroup>
                  </EditorSectionItem>
                </EditorSectionCard>
              </EditorSidebarSection>

              {/* Media */}
              <EditorSidebarSection heading="Bilder">
                <EditorSectionCard>
                  <EditorSectionItem
                    title="Butiksbilder"
                    icon={ImagePlus}
                    defaultOpen
                  >
                    <MediaImageManager
                      ownerType="producer_shop"
                      ownerId={farmId}
                      producerId={farmId}
                      draftSessionId=""
                      coverUpload={shopCoverUpload}
                      galleryUpload={shopGalleryUpload}
                      helperText="Ladda upp omslagsbild och galleri för butiken."
                      sectionLabel="Butiksbilder"
                    />
                  </EditorSectionItem>
                </EditorSectionCard>
              </EditorSidebarSection>

              {/* Products quick access */}
              <EditorSidebarSection heading="Produkter">
                <EditorSectionCard>
                  <EditorSectionItem
                    title="Hantera produkter"
                    icon={Package}
                    hint={`${activeProducts.length} aktiva, ${draftProducts.length} utkast`}
                    defaultOpen
                  >
                    <div className="space-y-3">
                      <div className="grid grid-cols-2 gap-2 text-center">
                        <div className="rounded-lg bg-muted/50 p-3">
                          <p className="text-2xl font-semibold text-foreground">
                            {productsLoading ? '—' : activeProducts.length}
                          </p>
                          <p className="text-xs text-muted-foreground">Aktiva</p>
                        </div>
                        <div className="rounded-lg bg-muted/50 p-3">
                          <p className="text-2xl font-semibold text-foreground">
                            {productsLoading ? '—' : draftProducts.length}
                          </p>
                          <p className="text-xs text-muted-foreground">Utkast</p>
                        </div>
                      </div>
                      <div className="flex gap-2">
                        <Button size="sm" variant="outline" className="flex-1 text-xs" asChild>
                          <Link to={`/producer/farm/${farmId}/shop/products`}>
                            <Package className="h-3.5 w-3.5 mr-1" />
                            Alla produkter
                          </Link>
                        </Button>
                        <Button size="sm" className="flex-1 text-xs" asChild>
                          <Link to={`/producer/farm/${farmId}/shop/products/new`}>
                            <Plus className="h-3.5 w-3.5 mr-1" />
                            Ny produkt
                          </Link>
                        </Button>
                      </div>
                    </div>
                  </EditorSectionItem>
                </EditorSectionCard>
              </EditorSidebarSection>

              {/* KPIs */}
              <EditorSidebarSection heading="Orderöversikt">
                <EditorSectionCard>
                  <EditorSectionItem
                    title="Statistik"
                    icon={BarChart3}
                    hint={kpisLoading ? '' : `${kpis?.orders_count ?? 0} ordrar`}
                  >
                    <div className="space-y-3">
                      <Select value={String(kpiDays)} onValueChange={(v) => setKpiDays(Number(v) as KpiPeriod)}>
                        <SelectTrigger className="w-full h-8 text-xs">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {KPI_PERIOD_OPTIONS.map((opt) => (
                            <SelectItem key={opt.value} value={String(opt.value)}>
                              {opt.label}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <div className="grid grid-cols-2 gap-2 text-center">
                        <div className="rounded-lg bg-muted/50 p-2.5">
                          <p className="text-lg font-semibold text-foreground">
                            {kpisLoading ? '—' : kpis?.orders_count ?? 0}
                          </p>
                          <p className="text-[11px] text-muted-foreground">Ordrar</p>
                        </div>
                        <div className="rounded-lg bg-muted/50 p-2.5">
                          <p className="text-lg font-semibold text-foreground">
                            {kpisLoading ? '—' : formatMoney(kpis?.expected_total_ore ?? 0)}
                          </p>
                          <p className="text-[11px] text-muted-foreground">Förväntat</p>
                        </div>
                      </div>
                      <Button variant="link" size="sm" className="text-xs text-muted-foreground w-full" asChild>
                        <Link to={`/producer/farm/${farmId}/shop/orders?days=${kpiDays}`}>
                          Se ordrar
                          <ArrowRight className="ml-1 h-3.5 w-3.5" />
                        </Link>
                      </Button>
                    </div>
                  </EditorSectionItem>
                </EditorSectionCard>
              </EditorSidebarSection>
            </div>
          }
        />
      </div>

      {/* Farm Shop Share Studio */}
      {producer && (
        <ShareStudioSheet
          open={showShareStudio}
          onOpenChange={setShowShareStudio}
          target={mapFarmshopToShareTarget({
            producerId: producer.id,
            producerName: producer.name,
            producerSlug: producer.slug || '',
            heroImageUrl: resolvedHeroUrl,
            shopIntro: producer.farm_shop_intro,
            openingHoursText: producer.farm_shop_opening_hours_text,
          })}
        />
      )}

      {/* Mobile sticky bottom bar when shop is hidden */}
      {shopIsHidden && (
        <div className="fixed bottom-0 inset-x-0 z-40 border-t bg-background/95 backdrop-blur-sm p-3 md:hidden safe-area-pb">
          <Button
            className="w-full"
            onClick={handleActivateShop}
            disabled={activating}
          >
            {activating ? (
              <>
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                Aktiverar…
              </>
            ) : (
              <>
                <Store className="h-4 w-4 mr-2" />
                Aktivera gårdsbutik
              </>
            )}
          </Button>
        </div>
      )}
    </ProducerLayout>
  );
}
